"use client";

import React from "react";
import { MapPin, Phone, Mail, Clock, Facebook, Twitter, Linkedin, Instagram, Loader, Send } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
const StyledInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({
  className,
  ...props
}, ref) => {
  return <input className={`flex h-12 w-full rounded-2xl border-b-4 border-secondary/30 bg-white px-4 py-2 text-base text-foreground ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/40 focus-visible:border-primary focus-visible:bg-white focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 shadow-sm transition-all duration-300 ${className}`} ref={ref} {...props} />;
});
StyledInput.displayName = "StyledInput";
const StyledTextarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({
  className,
  ...props
}, ref) => {
  return <textarea className={`flex min-h-[120px] w-full rounded-2xl border-b-4 border-secondary/30 bg-white px-4 py-2 text-base text-foreground ring-offset-background placeholder:text-muted-foreground/40 focus-visible:border-primary focus-visible:bg-white focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 shadow-sm transition-all duration-300 resize-y ${className}`} ref={ref} {...props} />;
});
StyledTextarea.displayName = "StyledTextarea";
export default function StartAConversation() {
  return <section data-section-id="20555" id="say-hola" className="w-full bg-background text-foreground">
      <div className="flex flex-col lg:flex-row min-h-screen">
        
        {}
        <div className="w-full lg:w-[40%] bg-secondary/5 p-8 lg:p-16 flex flex-col justify-center border-r border-border/50">
          <div className="max-w-md mx-auto w-full space-y-12">
            
            {}
            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground tracking-tight">
                Say Hola!
              </h2>
              <p className="text-muted-foreground text-xl font-sans leading-relaxed">
                Have a question about our magical Spanish classes? We'd love to hear from you and help your little learner start their adventure!
              </p>
            </div>

            {}
            <div className="space-y-6">
              <div className="flex items-start gap-4 group">
                <div className="p-4 bg-primary/10 shadow-md border border-primary/20 rounded-2xl shrink-0 group-hover:scale-110 transition-transform">
                  <MapPin className="size-6 text-primary" />
                </div>
                <div>
                  <h4 className="font-serif text-lg text-foreground">Our Playroom</h4>
                  <p className="text-muted-foreground mt-1">123 Sunshine Lane, Storybook City</p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="p-4 bg-secondary/10 shadow-md border border-secondary/20 rounded-2xl shrink-0 group-hover:scale-110 transition-transform">
                  <Mail className="size-6 text-secondary" />
                </div>
                <div>
                  <h4 className="font-serif text-lg text-foreground">Send a Paper Plane</h4>
                  <Link to="mailto:hello@linguakids.com" className="text-muted-foreground mt-1 hover:text-secondary transition-colors block">
                    hello@linguakids.com
                  </Link>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="p-4 bg-destructive/10 shadow-md border border-destructive/20 rounded-2xl shrink-0 group-hover:scale-110 transition-transform">
                  <Phone className="size-6 text-destructive" />
                </div>
                <div>
                  <h4 className="font-serif text-lg text-foreground">Ring Our Bell</h4>
                  <Link to="tel:+12345678900" className="text-muted-foreground mt-1 hover:text-destructive transition-colors block">
                    +1 (555) FUN-SPANISH
                  </Link>
                </div>
              </div>
            </div>

            {}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-foreground font-serif text-lg">
                <Clock className="size-5 text-primary" />
                <span>When We're Playing</span>
              </div>
              <div className="border border-border rounded-3xl overflow-hidden bg-white shadow-lg">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                      <TableHead className="w-[120px] font-serif">Day</TableHead>
                      <TableHead className="font-serif">Hours</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">Mon - Fri</TableCell>
                      <TableCell>8:00 AM - 6:00 PM</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Saturday</TableCell>
                      <TableCell>9:00 AM - 2:00 PM</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Sunday</TableCell>
                      <TableCell className="text-muted-foreground">Family Time</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </div>

            {}
            <div className="flex gap-4 pt-4">
              {[{
              icon: Linkedin,
              href: "#"
            }, {
              icon: Twitter,
              href: "#"
            }, {
              icon: Instagram,
              href: "#"
            }, {
              icon: Facebook,
              href: "#"
            }].map((social, i) => <Link key={i} to={social.href} className="p-3 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-2xl transition-all shadow-sm hover:shadow-md" aria-label="Social Link">
                  <social.icon className="size-6" />
                </Link>)}
            </div>

          </div>
        </div>

        {}
        <div className="w-full lg:w-[60%] bg-primary text-white p-8 lg:p-16 flex flex-col justify-center relative overflow-hidden">
          {}
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-secondary/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2 pointer-events-none" />

          <div className="max-w-xl mx-auto w-full relative z-10">
            <div className="mb-10">
              <h3 className="text-3xl md:text-4xl font-serif font-bold text-white mb-3">Send a Magic Message</h3>
              <p className="text-white/90 text-lg font-sans">
                Tell us about your child, and we'll get back to you with a sprinkle of magic!
              </p>
            </div>

            <FormProvider formKey="say_hola_form_contact" formId="say_hola_form_contact" sectionName="connect-section" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            topic: "",
            message: ""
          }} validationRules={{
            full_name: {
              required: "Name is required"
            },
            email: {
              required: "Email is required",
              pattern: "email"
            },
            phone: {
              pattern: "phone"
            },
            topic: {
              required: "Please select a topic"
            },
            message: {
              required: "Message is required",
              minLength: 20,
              maxLength: 1000
            }
          }} submitText="Send Magic Message!" successMessage="Yay! Your message is on its way. We'll talk soon!" errorMessage="Oops! The magic failed. Please try again." className="w-full">
              <ContactForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-white/10 border-2 border-white/30 backdrop-blur-sm rounded-3xl text-center animate-in fade-in zoom-in duration-500">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white text-primary mb-6 shadow-xl">
          <Send className="size-10" />
        </div>
        <h3 className="text-2xl font-serif font-bold text-white mb-3">Message Sent!</h3>
        <p className="text-white/90 text-lg mb-8">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-white/40 text-white hover:bg-white hover:text-primary rounded-full px-8 h-12 font-serif text-lg transition-all">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className="text-white/90 text-sm uppercase tracking-widest font-serif ml-1">Parent's Name</FormLabel>
              <FormControl>
                <StyledInput placeholder="Mama or Papa Bear" {...field} />
              </FormControl>
              <FormMessage className="text-red-200 font-medium" />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className="text-white/90 text-sm uppercase tracking-widest font-serif ml-1">Email Address</FormLabel>
              <FormControl>
                <StyledInput type="email" placeholder="hello@example.com" {...field} />
              </FormControl>
              <FormMessage className="text-red-200 font-medium" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className="text-white/90 text-sm uppercase tracking-widest font-serif ml-1">Phone Number <span className="text-white/60 normal-case tracking-normal ml-1">(Optional)</span></FormLabel>
              <FormControl>
                <StyledInput type="tel" placeholder="555-123-4567" {...field} />
              </FormControl>
              <FormMessage className="text-red-200 font-medium" />
            </FormItem>} />

        <FormField control={control} name="topic" render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className="text-white/90 text-sm uppercase tracking-widest font-serif ml-1">What are you curious about?</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="h-12 rounded-2xl border-b-4 border-secondary/30 bg-white text-foreground focus:ring-0 focus:ring-offset-0 focus:border-primary border-t-0 border-x-0 px-4">
                    <SelectValue placeholder="Pick a topic" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-white border-secondary/20 text-foreground rounded-2xl shadow-xl">
                  {["Spanish for Beginners", "Advanced Adventures", "Trial Class Question", "Teacher Opportunities", "Something Else"].map((option, i) => <SelectItem key={i} value={option} className="focus:bg-secondary/10 focus:text-secondary-foreground cursor-pointer font-sans py-3">
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage className="text-red-200 font-medium" />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem className="space-y-1">
            <FormLabel className="text-white/90 text-sm uppercase tracking-widest font-serif ml-1">Your Message</FormLabel>
            <FormControl>
              <StyledTextarea placeholder="Tell us about your little learner..." {...field} />
            </FormControl>
            <FormDescription className="text-white/60 text-xs text-right font-sans">
              Min 20 magical characters
            </FormDescription>
            <FormMessage className="text-red-200 font-medium" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-500/20 border border-red-500/30 rounded-2xl backdrop-blur-sm">
          <p className="text-white text-sm text-center font-medium">{submitError}</p>
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full h-16 text-xl font-serif bg-secondary hover:bg-secondary/90 text-white rounded-full shadow-[0_8px_0_rgb(125,211,252)] active:shadow-none active:translate-y-2 transition-all duration-200">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-6" />
              Sending Magic...
            </> : 'Send Magic Message!'}
        </Button>
      </div>
    </Form>;
}